import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { personService } from '../services/api';
import { Contact2, Plus, Building, User, Search, Trash2, X } from 'lucide-react';
import Notification from '../components/common/Notification';
import useNotification from '../hooks/useNotification';
import useModal from '../hooks/useModal';

const PERSON_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'individual', label: 'Individual' },
  { value: 'business', label: 'Business' },
  { value: 'organization', label: 'Organization' },
];

const CONTACT_TYPE_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'phone_cell', label: 'Cell Phone' },
  { value: 'phone_home', label: 'Home Phone' },
  { value: 'phone_work', label: 'Work Phone' },
  { value: 'fax', label: 'Fax' },
];

const Persons = () => {
  const navigate = useNavigate();
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(showCreateModal, () => setShowCreateModal(false));

  // Create form state
  const [createForm, setCreateForm] = useState({
    person_type: 'individual',
    first_name: '',
    last_name: '',
    company_name: '',
    notes: '',
    contacts: [],
  });

  const fetchPersons = useCallback(async () => {
    try {
      const params = {};
      if (typeFilter) params.person_type = typeFilter;
      const response = await personService.getAll(params);
      setPersons(response.data || []);
    } catch (error) {
      console.error('Error fetching persons:', error);
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    fetchPersons();
  }, [fetchPersons]);

  const filteredPersons = useMemo(() => {
    if (!searchFilter.trim()) return persons;
    const searchLower = searchFilter.toLowerCase().trim();
    return persons.filter(person => {
      const fullName = `${person.first_name || ''} ${person.last_name || ''}`.toLowerCase();
      const companyName = (person.company_name || '').toLowerCase();
      const contactValues = (person.contacts || []).map(c => c.value.toLowerCase()).join(' ');
      return fullName.includes(searchLower) ||
             companyName.includes(searchLower) ||
             contactValues.includes(searchLower);
    });
  }, [persons, searchFilter]);

  const getPrimaryContact = (person) => {
    if (!person.contacts || person.contacts.length === 0) return null;
    const primary = person.contacts.find(c => c.is_primary);
    return primary || person.contacts[0];
  };

  const getPersonName = (person) => {
    const parts = [person.first_name, person.last_name].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : person.company_name || 'Unnamed';
  };

  const resetCreateForm = () => {
    setCreateForm({
      person_type: 'individual',
      first_name: '',
      last_name: '',
      company_name: '',
      notes: '',
      contacts: [],
    });
  };

  const handleCreate = async () => {
    if (!createForm.first_name && !createForm.last_name && !createForm.company_name) {
      showError('At least one name field is required');
      return;
    }
    setCreateLoading(true);
    try {
      const data = {
        person_type: createForm.person_type,
        first_name: createForm.first_name || undefined,
        last_name: createForm.last_name || undefined,
        company_name: createForm.company_name || undefined,
        notes: createForm.notes || undefined,
        contacts: createForm.contacts
          .filter(c => c.value.trim())
          .map(c => ({
            contact_type: c.contact_type,
            value: c.value.trim(),
            label: c.label || undefined,
            is_primary: false,
          })),
      };
      await personService.create(data);
      setShowCreateModal(false);
      resetCreateForm();
      await fetchPersons();
      showSuccess('Person created successfully');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to create person');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDelete = async (personId, e) => {
    e.stopPropagation();
    try {
      await personService.delete(personId);
      setDeleteConfirm(null);
      await fetchPersons();
      showSuccess('Person deleted');
    } catch (error) {
      const detail = error.response?.data?.detail;
      showError(typeof detail === 'string' ? detail : 'Failed to delete person');
      setDeleteConfirm(null);
    }
  };

  const addCreateContact = () => {
    setCreateForm(prev => ({
      ...prev,
      contacts: [...prev.contacts, { contact_type: 'email', value: '', label: '' }],
    }));
  };

  const updateCreateContact = (index, field, value) => {
    setCreateForm(prev => ({
      ...prev,
      contacts: prev.contacts.map((c, i) => i === index ? { ...c, [field]: value } : c),
    }));
  };

  const removeCreateContact = (index) => {
    setCreateForm(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Contact2 className="mr-3 h-8 w-8" />
            Persons Directory
          </h1>
          <p className="text-gray-600">Browse and manage all persons across your firm</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-yellow-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-yellow-600"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Person
        </button>
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, company, or contact..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            {PERSON_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      {filteredPersons.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Primary Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPersons.map((person) => {
                  const primaryContact = getPrimaryContact(person);
                  return (
                    <tr
                      key={person.id}
                      onClick={() => navigate(`/persons/${person.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="bg-gray-100 p-2 rounded-full">
                              {person.person_type === 'individual' ? (
                                <User className="h-6 w-6 text-yellow-600" />
                              ) : (
                                <Building className="h-6 w-6 text-yellow-600" />
                              )}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {getPersonName(person)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600 capitalize">{person.person_type}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {person.company_name || <span className="text-gray-400">-</span>}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {primaryContact ? (
                          <span className="text-sm text-gray-600">{primaryContact.value}</span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {deleteConfirm === person.id ? (
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => handleDelete(person.id, e)}
                              className="text-xs text-red-600 hover:text-red-800 font-medium"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteConfirm(null); }}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirm(person.id); }}
                            className="text-gray-400 hover:text-red-500"
                            title="Delete person"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Contact2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500">
            {searchFilter || typeFilter ? 'No persons match your search' : 'No persons found'}
          </p>
        </div>
      )}

      {/* Create Person Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          onMouseDown={handleBackdropMouseDown}
          onMouseUp={handleBackdropMouseUp}
        >
          <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-50 -z-10" aria-hidden="true"></div>
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[85vh] flex flex-col"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">New Person</h2>
              <button onClick={() => { setShowCreateModal(false); resetCreateForm(); }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={createForm.person_type}
                  onChange={(e) => setCreateForm({ ...createForm, person_type: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="individual">Individual</option>
                  <option value="business">Business</option>
                  <option value="organization">Organization</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={createForm.first_name}
                    onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={createForm.last_name}
                    onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={createForm.company_name}
                  onChange={(e) => setCreateForm({ ...createForm, company_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
                />
              </div>

              {/* Contacts */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Contact Methods</label>
                  <button
                    type="button"
                    onClick={addCreateContact}
                    className="flex items-center gap-1 text-xs text-yellow-700 hover:text-yellow-800"
                  >
                    <Plus size={14} /> Add Contact
                  </button>
                </div>
                {createForm.contacts.length === 0 && (
                  <p className="text-xs text-gray-400">No contacts added yet.</p>
                )}
                {createForm.contacts.map((contact, index) => (
                  <div key={index} className="flex items-center gap-2 mb-2">
                    <select
                      value={contact.contact_type}
                      onChange={(e) => updateCreateContact(index, 'contact_type', e.target.value)}
                      className="border border-gray-300 rounded-md px-2 py-1.5 text-sm w-32"
                    >
                      {CONTACT_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={contact.value}
                      onChange={(e) => updateCreateContact(index, 'value', e.target.value)}
                      placeholder={contact.contact_type === 'email' ? 'email@example.com' : '(555) 123-4567'}
                      className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                    />
                    <button type="button" onClick={() => removeCreateContact(index)} className="text-gray-400 hover:text-red-500">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t flex-shrink-0">
              <button
                onClick={() => { setShowCreateModal(false); resetCreateForm(); }}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createLoading}
                className="px-4 py-2 text-sm text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:opacity-50"
              >
                {createLoading ? 'Creating...' : 'Create Person'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default Persons;
